/**
 * AccountSummaryCards Component
 * 
 * Displays summary cards for Assets, Liabilities, and Total balance.
 * Includes a visibility toggle button for the Total card.
 */

import React from "react";
import { Box, Paper, Stack, Typography, IconButton } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

export interface Summary {
  assets: number;
  liabilities: number;
  total: number;
}

interface AccountSummaryCardsProps {
  summary: Summary;
  defaultCurrency: string;
  valuesVisible: boolean;
  formatCurrency: (amount: number, currency: string) => string;
  /** When set, value line shows only the number (no currency) for more space; user knows default currency */
  formatAmountOnly?: (amount: number, currency: string) => string;
  onToggleVisibility: () => void;
}

export default function AccountSummaryCards({
  summary,
  defaultCurrency,
  valuesVisible,
  formatCurrency,
  formatAmountOnly,
  onToggleVisibility,
}: AccountSummaryCardsProps): React.ReactElement {
  const labelRowMinHeight = "20px";
  const labelSx = { fontSize: "0.7rem", display: "block", lineHeight: 1.3, textAlign: "center" };
  const formatValue = formatAmountOnly ?? formatCurrency;
  const valueSx = {
    lineHeight: 1.2,
    fontSize: "0.875rem",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    textAlign: "center",
    width: "100%",
  };
  const cardSx = {
    flex: 1,
    minWidth: 0,
    p: { xs: 1, sm: 0.75 },
    border: 1,
    borderColor: "divider",
    bgcolor: "background.paper",
    borderRadius: 1.5,
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
  };

  return (
    <Stack direction="row" spacing={0.75} sx={{ width: "100%", alignItems: "stretch" }}>
      <Paper elevation={0} sx={cardSx}>
        <Box sx={{ mb: 0.25, minHeight: labelRowMinHeight }}>
          <Typography variant="caption" color="text.secondary" sx={labelSx}>
            Assets
          </Typography>
        </Box>
        <Typography variant="body1" color="primary.main" fontWeight="600" sx={valueSx} title={valuesVisible ? formatCurrency(summary.assets, defaultCurrency) : undefined}>
          {valuesVisible ? formatValue(summary.assets, defaultCurrency) : "•••••"}
        </Typography>
      </Paper>
      <Paper elevation={0} sx={cardSx}>
        <Box sx={{ mb: 0.25, minHeight: labelRowMinHeight }}>
          <Typography variant="caption" color="text.secondary" sx={labelSx}>
            Liabilities
          </Typography>
        </Box>
        <Typography variant="body1" color="error.main" fontWeight="600" sx={valueSx} title={valuesVisible ? formatCurrency(summary.liabilities, defaultCurrency) : undefined}>
          {valuesVisible ? formatValue(summary.liabilities, defaultCurrency) : "•••••"}
        </Typography>
      </Paper>
      <Paper elevation={0} sx={{ ...cardSx, position: "relative" }}>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 0.5, mb: 0.25, minHeight: labelRowMinHeight }}>
          <Typography variant="caption" color="text.secondary" sx={labelSx}>
            Total
          </Typography>
          <IconButton
            onClick={onToggleVisibility}
            size="small"
            sx={{
              p: 0.125,
              minWidth: "auto",
              width: 20,
              height: 20,
              color: "text.secondary",
              "&:hover": {
                color: "primary.main",
                bgcolor: "action.hover",
              },
            }}
            title={valuesVisible ? "Hide values" : "Show values"}
          >
            {valuesVisible ? (
              <VisibilityIcon sx={{ fontSize: "0.875rem" }} />
            ) : (
              <VisibilityOffIcon sx={{ fontSize: "0.875rem" }} />
            )}
          </IconButton>
        </Box>
        <Typography
          variant="body1"
          color="success.main"
          fontWeight="600"
          sx={valueSx}
          title={valuesVisible ? formatCurrency(summary.total, defaultCurrency) : undefined}
        >
          {valuesVisible ? formatValue(summary.total, defaultCurrency) : "•••••"}
        </Typography>
      </Paper>
    </Stack>
  );
}
